import type { SharedSlice } from "@prismicio/types-internal/lib/customtypes"
import { useRef, useState } from "react"

import { createSlices as newCreateAiSlicesRequest } from "../../../../app/services/ai"
import { useRepository } from "../../../../app/services/repository"
import {
  trackSlicePromptedDone,
  trackSlicePromptedStarted,
} from "../../../../app/services/tracking"
import { useDocument } from "../../../DocumentProvider"
import {
  getSliceUUIDFromKey,
  type SliceConfigWithSharedSlice,
  type VisualSharedSlice,
} from "../sliceTableData"
import { createSharedSlice } from "../utils/createSlice"

interface UseCreateAiSlicesArgs {
  slices: SliceConfigWithSharedSlice[]
}

export type UseCreateAiSlicesResult = CreateAiSlicesState & {
  prompt: string
  loading: boolean
  disabled: boolean
  selectedSlice?: SharedSlice
  setPrompt: (newPrompt: string) => void
  create: () => void
  clear: () => void
}

export function useCreateAiSlices(
  args: UseCreateAiSlicesArgs,
): UseCreateAiSlicesResult {
  const { slices: sliceOptions } = args

  const { data, documentId, draft, locale } = useDocument()

  const [rawPrompt, setRawPrompt] = useState("")
  const prompt = rawPrompt.trim()

  const [slices, setSlices] = useState<CreateAiSlicesState>({
    status: "idle",
  })

  const loading = ["slicing", "filling"].includes(slices.status)
  const disabled = loading || !prompt

  const { simulatorUrl } = useRepository()

  const promptIdRef = useRef<string>()

  function create() {
    if (disabled) return

    promptIdRef.current = undefined

    if (!simulatorUrl) {
      return setSlices({ status: "error", error: "simulator" })
    }

    setSlices(
      selectedSlice
        ? {
            status: "filling",
            slice: findSlice({
              slice: selectedSlice.id,
              slices: sliceOptions,
            }),
          }
        : { status: "slicing" },
    )

    const promptId = crypto.randomUUID()
    promptIdRef.current = promptId

    const started = Date.now()

    const trackingInfo = {
      customTypeId: data.customType.id,
      ...(documentId && { documentId }),
      prompt,
      promptId,
      locale,
    }

    trackSlicePromptedStarted(trackingInfo)

    newCreateAiSlicesRequest({
      uuid: promptId,
      slices: sliceOptions,
      document: draft.document,
      prompt,
      locale,
      onMessage: (message) => {
        // Ignore messages if the prompt is not relevant anymore.
        if (promptId !== promptIdRef.current) return

        switch (message.status) {
          case "slicing":
            setSlices(message)
            break
          case "filling":
            setSlices({
              ...message,
              slice: findSlice({ slice: message.slice, slices: sliceOptions }),
            })
            break
          case "done": {
            const { slice: sliceId, slices, cost } = message

            trackSlicePromptedDone({
              ...trackingInfo,
              slices: String(slices.length),
              cost: String(cost),
              time: ((Date.now() - started) / 1000).toFixed(1),
            })

            const config = sliceOptions.find(
              ({ sliceConfig }) => sliceConfig.id === sliceId,
            )
            if (!config) throw new Error(`Unknown slice "${sliceId}" chosen.`)

            setSlices({
              status: "done",
              simulatorUrl,
              promptId,
              slices: slices.map((slice) => {
                const variation = config.sliceConfig.variations.find(
                  (variation) => variation.id === slice.variation,
                )
                if (!variation)
                  throw new Error(
                    `Unknown variation "${slice.variation}" for slice "${sliceId}".`,
                  )

                const sliceItemContent = createSharedSlice({
                  sliceId,
                  variationId: slice.variation,
                  withContent: slice,
                })

                const uuid = getSliceUUIDFromKey(sliceItemContent.key)
                if (!uuid)
                  throw new Error(`Wrong slice key "${sliceItemContent.key}".`)

                return {
                  type: "shared",
                  zoneId: config.zoneId,
                  key: sliceItemContent.key,
                  name: sliceItemContent.name,
                  config: config.sliceConfig,
                  uuid,
                  variation,
                  content: slice,
                  canMoveUp: false,
                  canMoveDown: false,
                }
              }),
            })
            break
          }
          case "failed":
            setSlices({ status: "error", error: "request" })
            break
        }
      },
    })
  }

  function clear() {
    setRawPrompt("")
    promptIdRef.current = undefined
    setSlices({ status: "idle" })
  }

  const selectedSlice =
    sliceOptions.length > 1 ? undefined : sliceOptions[0]?.sliceConfig

  return {
    ...slices,
    prompt: rawPrompt,
    setPrompt: setRawPrompt,
    loading,
    disabled,
    selectedSlice,
    create,
    clear,
  }
}

type CreateAiSlicesState =
  | { status: "idle" }
  | { status: "slicing" }
  | { status: "filling"; slice: { id: string; name: string } }
  | {
      status: "done"
      simulatorUrl: string
      promptId: string
      slices: VisualSharedSlice[]
    }
  | { status: "error"; error: "request" | "simulator" }

function findSlice(params: {
  slice: string
  slices: SliceConfigWithSharedSlice[]
}) {
  const { slice: sliceId, slices } = params
  const slice = slices.find(({ sliceConfig }) => sliceConfig.id === sliceId)
  if (!slice) throw new Error(`Unknown slice "${sliceId}".`)
  return { id: slice.sliceConfig.id, name: slice.sliceConfig.name }
}
